import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProcessSection = () => {
  const [activeStep, setActiveStep] = useState(1);

  const processSteps = [
    {
      id: 1,
      title: "Discovery & Consultation",
      subtitle: "Understanding your vision",
      icon: "MessageCircle",
      duration: "1-2 days",
      description: "We start with a comprehensive consultation to understand your business goals, target audience, and project requirements. Our team conducts thorough research to ensure we're aligned with your vision.",
      activities: [
        "Initial consultation call",
        "Requirements gathering",
        "Market research",
        "Competitor analysis",
        "Project scope definition"
      ],
      deliverables: [
        "Project brief document",
        "Timeline and milestones",
        "Resource allocation plan"
      ],
      color: "blue"
    },
    {
      id: 2,
      title: "Strategy & Planning",
      subtitle: "Crafting the perfect approach",
      icon: "Target",
      duration: "2-3 days",
      description: "Based on our discovery phase, we develop a comprehensive strategy and detailed project plan. This includes wireframes, user journeys, and technical specifications tailored to your specific needs.",
      activities: [
        "Strategy development",
        "Wireframe creation",
        "User experience mapping",
        "Technical architecture",
        "Resource planning"
      ],
      deliverables: [
        "Project strategy document",
        "Wireframes and mockups",
        "Technical specifications",
        "Project timeline"
      ],
      color: "green"
    },
    {
      id: 3,
      title: "Design & Development",
      subtitle: "Bringing ideas to life",
      icon: "Palette",
      duration: "5-14 days",
      description: "Our expert team begins the creative process, developing your project with regular check-ins and updates. We maintain open communication throughout to ensure everything meets your expectations.",
      activities: [
        "Design concept creation",
        "Development implementation",
        "Regular progress updates",
        "Quality assurance testing",
        "Client feedback integration"
      ],
      deliverables: [
        "Design concepts",
        "Development milestones",
        "Progress reports",
        "Beta version for review"
      ],
      color: "purple"
    },
    {
      id: 4,
      title: "Review & Refinement",
      subtitle: "Perfecting every detail",
      icon: "RefreshCw",
      duration: "2-3 days",
      description: "We present the completed work for your review and incorporate any feedback. Our unlimited revision policy ensures you\'re completely satisfied with the final result before we proceed to launch.",
      activities: [
        "Project presentation",
        "Client feedback collection",
        "Revision implementation",
        "Final quality checks",
        "Performance optimization"
      ],
      deliverables: [
        "Complete project review",
        "Revision documentation",
        "Final quality report",
        "Performance metrics"
      ],
      color: "orange"
    },
    {
      id: 5,
      title: "Launch & Support",
      subtitle: "Going live with confidence",
      icon: "Rocket",
      duration: "1-2 days",
      description: "We handle the complete launch process and provide ongoing support to ensure everything runs smoothly. Our team remains available for any questions or additional needs that may arise.",
      activities: [
        "Final deployment",
        "Launch coordination",
        "Performance monitoring",
        "Support documentation",
        "Team training (if needed)"
      ],
      deliverables: [
        "Live project launch",
        "Support documentation",
        "Training materials",
        "Maintenance guidelines"
      ],
      color: "red"
    }
  ];

  const getColorClasses = (color, isActive = false) => {
    const colors = {
      blue: {
        bg: isActive ? 'bg-blue-600' : 'bg-blue-100',
        text: isActive ? 'text-white' : 'text-blue-600',
        border: 'border-blue-200',
        accent: 'bg-blue-50'
      },
      green: {
        bg: isActive ? 'bg-green-600' : 'bg-green-100',
        text: isActive ? 'text-white' : 'text-green-600',
        border: 'border-green-200',
        accent: 'bg-green-50'
      },
      purple: {
        bg: isActive ? 'bg-purple-600' : 'bg-purple-100',
        text: isActive ? 'text-white' : 'text-purple-600',
        border: 'border-purple-200',
        accent: 'bg-purple-50'
      },
      orange: {
        bg: isActive ? 'bg-orange-600' : 'bg-orange-100',
        text: isActive ? 'text-white' : 'text-orange-600',
        border: 'border-orange-200',
        accent: 'bg-orange-50'
      },
      red: {
        bg: isActive ? 'bg-red-600' : 'bg-red-100',
        text: isActive ? 'text-white' : 'text-red-600',
        border: 'border-red-200',
        accent: 'bg-red-50'
      }
    };
    return colors[color];
  };

  return (
    <section id="process" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mb-6">
            Our Proven
            <span className="text-blue-600"> 5-Step Process</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From initial consultation to final launch, our streamlined process ensures quality results and transparent communication every step of the way.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Desktop Timeline */}
          <div className="hidden lg:block">
            <div className="flex justify-between items-center mb-12">
              {processSteps.map((step, index) => {
                const isActive = activeStep === step.id;
                const isCompleted = activeStep > step.id;
                const colorClasses = getColorClasses(step.color, isActive);
                
                return (
                  <div key={step.id} className="flex flex-col items-center relative">
                    {/* Connector Line */}
                    {index < processSteps.length - 1 && (
                      <div className={`absolute top-8 left-16 w-32 h-1 ${
                        isCompleted ? 'bg-green-400' : 'bg-gray-200'
                      } transition-colors duration-300`}></div>
                    )}
                    
                    {/* Step Circle */}
                    <button
                      onClick={() => setActiveStep(step.id)}
                      className={`w-16 h-16 rounded-full ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center font-bold text-lg transition-all duration-300 hover:scale-110 relative z-10 shadow-lg`}
                    >
                      {isCompleted ? (
                        <Icon name="Check" size={24} />
                      ) : (
                        <Icon name={step.icon} size={24} />
                      )}
                    </button>
                    
                    {/* Step Info */}
                    <div className="text-center mt-4 max-w-32">
                      <div className={`text-sm font-semibold ${isActive ? 'text-blue-600' : 'text-gray-900'}`}>
                        Step {step.id}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {step.duration}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden mb-8">
            <div className="flex overflow-x-auto pb-4 space-x-4">
              {processSteps.map((step) => {
                const isActive = activeStep === step.id;
                const colorClasses = getColorClasses(step.color, isActive);
                
                return (
                  <button
                    key={step.id}
                    onClick={() => setActiveStep(step.id)}
                    className={`flex-shrink-0 w-16 h-16 rounded-full ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center transition-all duration-300`}
                  >
                    <Icon name={step.icon} size={24} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Step Details */}
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            {processSteps.map((step) => {
              if (step.id !== activeStep) return null;
              
              const colorClasses = getColorClasses(step.color);
              
              return (
                <div key={step.id} className="p-8 lg:p-12">
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Left Column - Main Content */}
                    <div>
                      {/* Header */}
                      <div className="flex items-center mb-6">
                        <div className={`w-16 h-16 rounded-2xl ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center mr-4`}>
                          <Icon name={step.icon} size={32} />
                        </div>
                        <div>
                          <div className="text-sm text-gray-600 font-semibold">
                            Step {step.id} • {step.duration}
                          </div>
                          <h3 className="text-2xl lg:text-3xl font-bold font-poppins text-gray-900">
                            {step.title}
                          </h3>
                          <p className="text-lg text-gray-600">
                            {step.subtitle}
                          </p>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-700 leading-relaxed mb-8">
                        {step.description}
                      </p>

                      {/* Activities */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                          What We Do:
                        </h4>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {step.activities.map((activity, index) => (
                            <div key={index} className="flex items-center">
                              <Icon name="ArrowRight" size={16} className="text-blue-600 mr-3 flex-shrink-0" />
                              <span className="text-gray-700 text-sm">{activity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Deliverables */}
                    <div>
                      <div className={`${colorClasses.accent} rounded-2xl p-6 h-full`}>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <Icon name="Package" size={20} className="mr-2" />
                          Deliverables
                        </h4>
                        <div className="space-y-4">
                          {step.deliverables.map((deliverable, index) => (
                            <div key={index} className="flex items-start">
                              <div className={`w-6 h-6 rounded-full ${colorClasses.bg} ${colorClasses.text} flex items-center justify-center mr-3 mt-0.5 flex-shrink-0`}>
                                <Icon name="Check" size={14} />
                              </div>
                              <span className="text-gray-700">{deliverable}</span>
                            </div>
                          ))}
                        </div>

                        {/* Progress Indicator */}
                        <div className="mt-8">
                          <div className="flex justify-between text-sm text-gray-600 mb-2">
                            <span>Progress</span>
                            <span>{Math.round((activeStep / processSteps.length) * 100)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${(activeStep / processSteps.length) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center mt-8 space-x-4">
            <button
              onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
              disabled={activeStep === 1}
              className="flex items-center px-6 py-3 bg-white border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeft" size={20} className="mr-2" />
              Previous
            </button>
            <button
              onClick={() => setActiveStep(Math.min(processSteps.length, activeStep + 1))}
              disabled={activeStep === processSteps.length}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <Icon name="ChevronRight" size={20} className="ml-2" />
            </button>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="bg-blue-600 text-white rounded-3xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold font-poppins mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              Our proven process has delivered success for over 5,000 projects. Let's discuss how we can help bring your vision to life.
            </p>
            <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-300 inline-flex items-center">
              Start Your Project
              <Icon name="ArrowRight" size={20} className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;